import isBrowser from 'util/isBrowser';
import { store } from 'store';
import {
  setPlayingTake,
  setLoadingTakeId,
  setPreloadedTakeId,
  setAudioPlayerMeta,
  setGeneratingSectionId,
} from 'store/ScriptStudioSlice';
import { toast } from 'components/Toaster';
import { captureExceptionWithState } from 'util/sentry';
import { getApolloClient } from 'lib/initApollo';
import fetch from '../fetch';
import {
  CreateAudioAccessTokenDocument,
  CreateAudioAccessTokenMutation,
  CreateAudioAccessTokenMutationVariables,
} from './operation.generated';

type StudioAudioHeaders = {
  Accept: 'audio/*';
  'X-WellSaid-Audio-Tag': '1';
};

export interface AudioEntry {
  audioElement: HTMLAudioElement;
  url: string;
  mime: string | null;
  takeId: string;
  sectionId?: string | null;
}

/**
 * AudioManager - Singleton class for managing audio elements outside React
 * Handles loading, playback, and disposal of take audio
 */
class StudioAudioManager {
  private static instance: StudioAudioManager;

  // If the browser refuses to grant a new MediaSource/WebMediaPlayer (e.g.
  // too many are already open in the tab), 'sourceopen' never fires and the
  // load would otherwise hang forever with no error. Match the timeout
  // pattern used by waitForMetadata below.
  private static readonly MEDIA_SOURCE_OPEN_TIMEOUT_MS = 10000;

  private audioElements: Map<string, AudioEntry> = new Map();

  private abortControllers: Map<string, AbortController | null> = new Map();

  private loadingPromises: Map<string, Promise<HTMLAudioElement>> = new Map();

  private pauseTimes: Map<string, number> = new Map();

  private eventListeners: Map<string, () => void> = new Map();

  private currentlyPlayingTakeId: string | null = null;

  private playRetries: Set<string> = new Set();

  private onEndedCallbacks: Set<
    (takeId: string, sectionId: string | null) => void
  > = new Set();

  private cancelGapCallbacks: Set<() => void> = new Set();

  registerOnEnded(
    cb: (takeId: string, sectionId: string | null) => void
  ): () => void {
    this.onEndedCallbacks.add(cb);
    return () => {
      this.onEndedCallbacks.delete(cb);
    };
  }

  registerCancelGap(cb: () => void): () => void {
    this.cancelGapCallbacks.add(cb);
    return () => {
      this.cancelGapCallbacks.delete(cb);
    };
  }

  cancelContinuousGap(): void {
    this.cancelGapCallbacks.forEach(cb => cb());
  }

  static getInstance(): StudioAudioManager {
    if (!StudioAudioManager.instance) {
      StudioAudioManager.instance = new StudioAudioManager();
    }
    return StudioAudioManager.instance;
  }

  /**
   * Validates and normalizes duration value
   * Returns 0 for NaN, Infinity, or non-positive values
   */
  private getValidDuration(duration: number): number {
    if (!Number.isFinite(duration) || duration <= 0) {
      return 0;
    }
    return duration;
  }

  /**
   * A pending HTMLMediaElement.play() promise rejects when the play request is
   * superseded by a pause() (or another load/play) before it resolves. The
   * browser reports this as an AbortError / "The play() request was interrupted
   * by a call to pause()" DOMException. It is expected during rapid play/pause
   * and generation transitions and must not be surfaced as a playback error.
   * @see https://developer.chrome.com/blog/play-request-was-interrupted
   */
  private isBenignPlayInterruption(error: unknown): boolean {
    const err = error as { name?: string; message?: string } | null;
    if (!err) return false;
    if (err.name === 'AbortError') return true;
    const message = err.message ?? '';
    return (
      /interrupted by a call to pause/i.test(message) ||
      /the play\(\) request/i.test(message) ||
      /request was interrupted/i.test(message)
    );
  }

  /**
   * Setup event listeners for an audio element
   */
  private setupEventListeners(
    takeId: string,
    audioElement: HTMLAudioElement
  ): void {
    // Clean up existing listeners for this take
    this.cleanupEventListeners(takeId);

    const handleTimeUpdate = () => {
      store.dispatch(
        setAudioPlayerMeta({
          currentTime: audioElement.currentTime || 0,
          duration: this.getValidDuration(audioElement.duration),
        })
      );
    };

    const handleDurationChange = () => {
      const validDuration = this.getValidDuration(audioElement.duration);
      if (validDuration > 0) {
        store.dispatch(
          setAudioPlayerMeta({
            duration: validDuration,
          })
        );
      }
    };

    const handleEnded = () => {
      const endedSectionId = this.audioElements.get(takeId)?.sectionId ?? null;
      if (this.currentlyPlayingTakeId === takeId) {
        this.currentlyPlayingTakeId = null;
      }
      store.dispatch(setPlayingTake({ takeId: null, sectionId: null }));
      this.onEndedCallbacks.forEach(cb => cb(takeId, endedSectionId));
    };

    const handleError = () => {
      const mediaError = audioElement.error;
      const sectionId = this.audioElements.get(takeId)?.sectionId ?? undefined;
      if (this.currentlyPlayingTakeId === takeId) {
        this.currentlyPlayingTakeId = null;
      }
      // eslint-disable-next-line no-console
      console.error(
        '[StudioAudioManager] Audio element error during playback',
        {
          takeId,
          sectionId,
          code: mediaError?.code,
          message: mediaError?.message,
        }
      );
      captureExceptionWithState(
        new Error(
          mediaError?.message ||
            `Audio element error (code ${mediaError?.code ?? 'unknown'})`
        ),
        {
          type: 'StudioAudioManager',
          value: {
            takeId,
            sectionId,
            phase: 'media-error',
            code: mediaError?.code,
          },
        }
      );
      store.dispatch(setAudioPlayerMeta({ error: true }));
      store.dispatch(setPlayingTake({ takeId: null, sectionId: null }));
      toast({
        message: 'Playback failed—check your connection and try again.',
        type: 'error',
      });
    };

    audioElement.addEventListener('timeupdate', handleTimeUpdate);
    audioElement.addEventListener('durationchange', handleDurationChange);
    audioElement.addEventListener('ended', handleEnded);
    audioElement.addEventListener('error', handleError);

    // Initial state dispatch
    // Note: For MediaSource streams, duration may be NaN until stream is finalized
    store.dispatch(
      setAudioPlayerMeta({
        currentTime: audioElement.currentTime || 0,
        duration: this.getValidDuration(audioElement.duration),
        error: false,
      })
    );

    // Store cleanup function
    const cleanupFn = () => {
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
      audioElement.removeEventListener('durationchange', handleDurationChange);
      audioElement.removeEventListener('ended', handleEnded);
      audioElement.removeEventListener('error', handleError);
    };

    this.eventListeners.set(takeId, cleanupFn);
  }

  /**
   * Cleanup event listeners for a take
   */
  private cleanupEventListeners(takeId: string): void {
    const cleanup = this.eventListeners.get(takeId);
    if (cleanup) {
      cleanup();
      this.eventListeners.delete(takeId);
    }
  }

  /**
   * Centralized playback state machine - ensures only one audio plays at a time
   * This is the single source of truth for starting playback
   */
  private async startPlayback(
    takeId: string,
    audioElement: HTMLAudioElement,
    sectionId?: string | null
  ): Promise<void> {
    // If this take is already playing, no-op
    if (this.currentlyPlayingTakeId === takeId) {
      return;
    }

    // Get sectionId from audioElements if not provided
    const resolvedSectionId =
      sectionId ?? this.audioElements.get(takeId)?.sectionId ?? null;

    // Pause any currently playing audio
    if (this.currentlyPlayingTakeId) {
      const currentAudio = this.audioElements.get(
        this.currentlyPlayingTakeId
      )?.audioElement;
      if (currentAudio && !currentAudio.paused) {
        currentAudio.pause();
        this.pauseTimes.set(
          this.currentlyPlayingTakeId,
          currentAudio.currentTime || 0
        );
      }
      // Clear Redux state for previous audio
      store.dispatch(setPlayingTake({ takeId: null, sectionId: null }));
    }

    // Update state machine and Redux immediately (before async play() call)
    // This ensures UI updates synchronously, especially important under throttled network conditions
    this.currentlyPlayingTakeId = takeId;
    store.dispatch(setPlayingTake({ takeId, sectionId: resolvedSectionId }));

    try {
      // Start playback
      await audioElement.play();

      // Verify state is still consistent after async play() call
      // (another play() might have been called during the async operation)
      if (this.currentlyPlayingTakeId !== takeId) {
        // Another audio started playing, pause this one
        audioElement.pause();
        this.pauseTimes.set(takeId, audioElement.currentTime || 0);
      }
    } catch (error) {
      // If play() fails, revert the state
      if (this.currentlyPlayingTakeId === takeId) {
        this.currentlyPlayingTakeId = null;
        store.dispatch(setPlayingTake({ takeId: null, sectionId: null }));
      }
      throw error;
    }
  }

  /**
   * Stop playback for a take and update state machine
   */
  private stopPlayback(takeId: string): void {
    if (this.currentlyPlayingTakeId === takeId) {
      this.currentlyPlayingTakeId = null;
      store.dispatch(setPlayingTake({ takeId: null, sectionId: null }));
    }
  }

  /**
   * Load audio for a take ID
   * This method loads audio
   */
  async loadAudio(
    takeId: string,
    options?: {
      headers?: StudioAudioHeaders | null;
      immediatePlayback?: boolean;
      sectionId?: string | null;
    }
  ): Promise<HTMLAudioElement> {
    const {
      headers = { Accept: 'audio/*', 'X-WellSaid-Audio-Tag': '1' },
      immediatePlayback = false,
      sectionId,
    } = options || {};

    // If audio already exists, return it (may have been silently preloaded)
    const existing = this.audioElements.get(takeId);
    if (existing?.audioElement) {
      if (!this.eventListeners.has(takeId)) {
        this.setupEventListeners(takeId, existing.audioElement);
      }
      store.dispatch(setPreloadedTakeId(takeId));
      store.dispatch(
        setAudioPlayerMeta({
          currentTime: existing.audioElement.currentTime || 0,
          duration: existing.audioElement.duration || 0,
          error: false,
        })
      );
      return existing.audioElement;
    }

    // If there's already a load in progress (may be a silent preload), wait for it
    const existingPromise = this.loadingPromises.get(takeId);
    if (existingPromise) {
      return existingPromise;
    }

    // Dispatch loading state
    store.dispatch(setLoadingTakeId(takeId));

    // Cancel any in-flight request for this take (shouldn't happen, but safety check)
    this.abortControllers.get(takeId)?.abort();

    const controller = new AbortController();
    this.abortControllers.set(takeId, controller);

    const loadPromise = this.loadFromServer(
      takeId,
      controller,
      headers,
      immediatePlayback,
      sectionId
    );
    this.loadingPromises.set(takeId, loadPromise);

    loadPromise
      .then(() => {
        this.loadingPromises.delete(takeId);
        store.dispatch(setPreloadedTakeId(takeId));
        store.dispatch(setAudioPlayerMeta({ error: false }));
        store.dispatch(setLoadingTakeId(null));
      })
      .catch(error => {
        this.loadingPromises.delete(takeId);
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        if (errorMessage && !errorMessage.includes('cancelled')) {
          toast({
            message: errorMessage,
            type: 'error',
          });
        }
        store.dispatch(setAudioPlayerMeta({ error: true }));
        store.dispatch(setLoadingTakeId(null));
      });

    return loadPromise;
  }

  /**
   * Silently fetch and cache an audio element without updating player state.
   * Used to warm the cache ahead of playback so play() can start instantly.
   */
  preloadAudio(takeId: string, sectionId?: string | null): void {
    if (this.audioElements.has(takeId)) return;
    if (this.loadingPromises.has(takeId)) return;

    this.abortControllers.get(takeId)?.abort();

    const controller = new AbortController();
    this.abortControllers.set(takeId, controller);

    const loadPromise = this.loadFromServer(
      takeId,
      controller,
      { Accept: 'audio/*', 'X-WellSaid-Audio-Tag': '1' },
      false,
      sectionId,
      true
    );
    this.loadingPromises.set(takeId, loadPromise);

    loadPromise
      .then(() => {
        this.loadingPromises.delete(takeId);
      })
      .catch(() => {
        this.loadingPromises.delete(takeId);
        this.cleanupTake(takeId);
      });
  }

  private async waitForMetadata(
    audioElement: HTMLAudioElement,
    errorMessage: string
  ): Promise<void> {
    if (audioElement.readyState >= 1) return;

    await new Promise<void>((resolve, reject) => {
      let timeoutId: number;

      const cleanup = () => {
        window.clearTimeout(timeoutId);
        audioElement.removeEventListener(
          'loadedmetadata',
          handleLoadedMetadata
        );
        audioElement.removeEventListener('error', handleError);
      };

      const handleLoadedMetadata = () => {
        cleanup();
        resolve();
      };

      const handleError = () => {
        cleanup();
        reject(new Error(errorMessage));
      };

      audioElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      audioElement.addEventListener('error', handleError);

      timeoutId = window.setTimeout(() => {
        cleanup();
        reject(new Error(`${errorMessage} (Timeout after 10s)`));
      }, 10000);
    });
  }

  private async initializeAudioElement(
    takeId: string,
    url: string,
    mime: string | null,
    sectionId?: string | null,
    silent?: boolean
  ): Promise<HTMLAudioElement> {
    const audioElement = new Audio(url);
    audioElement.preload = 'metadata';
    if (mime) audioElement.setAttribute('type', mime);

    const entry: AudioEntry = {
      audioElement,
      url,
      mime,
      takeId,
      sectionId: sectionId ?? null,
    };

    this.audioElements.set(takeId, entry);

    await this.waitForMetadata(audioElement, 'Failed to load audio metadata');

    const pauseTime = this.pauseTimes.get(takeId);
    if (pauseTime) {
      audioElement.currentTime = pauseTime;
    }

    if (!silent) {
      this.setupEventListeners(takeId, audioElement);
    }

    return audioElement;
  }

  /**
   * Shared settlement guard for loadFromStream/loadFromServerChunked: adds
   * a timeout for the case where 'sourceopen' never fires (the browser
   * refuses to grant a new MediaSource/WebMediaPlayer), and -- since the
   * audioElement/url are only registered in this.audioElements once
   * 'sourceopen' actually succeeds -- releases them directly on any other
   * settlement path instead of leaking them.
   */
  private createMediaSourceSettlement<T>(
    takeId: string,
    audioElement: HTMLAudioElement,
    url: string,
    res: (value: T) => void,
    rej: (error: Error) => void
  ): {
    resolve: (value: T) => void;
    reject: (error: Error) => void;
    isSettled: () => boolean;
    isRejected: () => boolean;
  } {
    let settled = false;
    let rejected = false;

    const releaseUnregisteredResources = () => {
      try {
        audioElement.pause();
        // eslint-disable-next-line no-param-reassign
        audioElement.src = '';
        // eslint-disable-next-line no-param-reassign
        audioElement.srcObject = null;
        audioElement.remove();
      } catch (e) {
        // Ignore errors releasing an already-detached element
      }
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // Ignore errors revoking an already-revoked URL
      }
    };

    let sourceOpenTimeoutId: number;

    const resolve = (value: T) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(sourceOpenTimeoutId);
      res(value);
    };
    const reject = (error: Error) => {
      if (settled) return;
      settled = true;
      rejected = true;
      window.clearTimeout(sourceOpenTimeoutId);
      if (!this.audioElements.has(takeId)) {
        releaseUnregisteredResources();
      }
      rej(error);
    };

    sourceOpenTimeoutId = window.setTimeout(() => {
      reject(
        new Error(
          `Timed out waiting for MediaSource to open (Timeout after ${
            StudioAudioManager.MEDIA_SOURCE_OPEN_TIMEOUT_MS / 1000
          }s)`
        )
      );
    }, StudioAudioManager.MEDIA_SOURCE_OPEN_TIMEOUT_MS);

    return {
      resolve,
      reject,
      isSettled: () => settled,
      isRejected: () => rejected,
    };
  }

  /**
   * Load incoming audio from a progressive stream using MediaSource API
   * This lets playback start before the entire audio file is downloaded.
   */
  async loadFromStream(
    takeId: string,
    mime: string,
    onReady: (
      appendChunk: (chunk: Uint8Array) => void,
      finalize: () => void
    ) => void,
    sectionId?: string | null
  ): Promise<HTMLAudioElement> {
    const existing = this.audioElements.get(takeId);
    if (existing?.audioElement) {
      return existing.audioElement;
    }

    const existingPromise = this.loadingPromises.get(takeId);
    if (existingPromise) {
      return existingPromise;
    }

    const loadPromise = new Promise<HTMLAudioElement>((res, rej) => {
      if (!isBrowser || typeof MediaSource === 'undefined') {
        rej(new Error('MediaSource API not available'));
        return;
      }

      const mediaSource = new MediaSource();
      const url = URL.createObjectURL(mediaSource);
      const audioElement = new Audio(url);

      const { resolve, reject, isSettled, isRejected } =
        this.createMediaSourceSettlement<HTMLAudioElement>(
          takeId,
          audioElement,
          url,
          res,
          rej
        );

      let sourceBuffer: SourceBuffer | null = null;
      const chunkQueue: Uint8Array[] = [];
      let isAppending = false;
      let isFinalized = false;
      let hasError = false;
      let hasStartedPlayback = false;

      const processQueue = () => {
        if (
          hasError ||
          isAppending ||
          !sourceBuffer ||
          sourceBuffer.updating ||
          chunkQueue.length === 0
        ) {
          return;
        }

        isAppending = true;
        const chunk = chunkQueue.shift()!;

        try {
          sourceBuffer.appendBuffer(chunk as BufferSource);
        } catch (error) {
          hasError = true;
          reject(
            new Error(
              `Failed to append buffer: ${
                error instanceof Error ? error.message : String(error)
              }`
            )
          );
        }
      };

      const appendChunk = (chunk: Uint8Array) => {
        if (hasError) return;
        chunkQueue.push(chunk);
        processQueue();
      };

      const finalize = () => {
        if (hasError) return;
        isFinalized = true;

        // If no chunks are pending and not currently appending, end the stream
        if (
          chunkQueue.length === 0 &&
          !isAppending &&
          mediaSource.readyState === 'open'
        ) {
          try {
            mediaSource.endOfStream();
          } catch (error) {
            // Ignore errors when ending stream
          }
        }
      };

      mediaSource.addEventListener('sourceopen', () => {
        // The timeout above may have already rejected this load; a
        // late-firing 'sourceopen' must be a no-op, not resurrect state
        // for a take the caller has already treated as failed.
        if (isSettled()) return;
        try {
          sourceBuffer = mediaSource.addSourceBuffer(mime);

          sourceBuffer.addEventListener('updateend', () => {
            isAppending = false;

            // If finalized and no more chunks, end the stream
            if (
              isFinalized &&
              chunkQueue.length === 0 &&
              mediaSource.readyState === 'open'
            ) {
              try {
                mediaSource.endOfStream();
              } catch (error) {
                // Ignore errors when ending stream
              }
            } else {
              // Process next chunk in queue
              processQueue();
            }
          });

          // Listen for when MediaSource ends - duration becomes available at this point
          mediaSource.addEventListener('sourceended', () => {
            // Duration should be available now, update it explicitly
            const validDuration = this.getValidDuration(audioElement.duration);
            if (validDuration > 0) {
              store.dispatch(
                setAudioPlayerMeta({
                  duration: validDuration,
                })
              );
            }
          });

          sourceBuffer.addEventListener('error', () => {
            hasError = true;
            reject(new Error('SourceBuffer error occurred'));
          });

          // Store entry and setup event listeners
          const entry: AudioEntry = {
            audioElement,
            url,
            mime,
            takeId,
            sectionId: sectionId ?? null,
          };
          this.audioElements.set(takeId, entry);
          this.setupEventListeners(takeId, audioElement);

          // Notify caller that we're ready to receive chunks
          onReady(appendChunk, finalize);

          // Resolve immediately - audio will start playing once enough data is buffered
          resolve(audioElement);
        } catch (error) {
          hasError = true;
          reject(
            new Error(
              `Failed to setup MediaSource: ${
                error instanceof Error ? error.message : String(error)
              }`
            )
          );
        }
      });

      mediaSource.addEventListener('error', () => {
        hasError = true;
        reject(new Error('MediaSource error occurred'));
      });

      // Handle audio element errors
      audioElement.addEventListener(
        'error',
        () => {
          hasError = true;
          reject(new Error('Audio element error occurred'));
        },
        { once: true }
      );

      // Start playback automatically when enough data is buffered
      const handleCanPlay = () => {
        // Ignore a late 'canplay' for a load the timeout already rejected --
        // otherwise audio could spontaneously start for a take the caller
        // was already shown an error for. (A normal 'canplay' fires *after*
        // a successful resolve(), so this checks isRejected(), not
        // isSettled().)
        if (isRejected()) return;
        if (!hasStartedPlayback && !hasError) {
          hasStartedPlayback = true;
          this.startPlayback(takeId, audioElement, sectionId ?? null)
            .then(() => {
              store.dispatch(setPreloadedTakeId(takeId));
              store.dispatch(setGeneratingSectionId(null));
            })
            .catch(error => {
              console.warn(
                'Auto-play failed (user interaction may be required)',
                { error, takeId }
              );
            });
        }
      };
      audioElement.addEventListener('canplay', handleCanPlay, { once: true });
    });

    this.loadingPromises.set(takeId, loadPromise);

    loadPromise
      .then(() => {
        this.loadingPromises.delete(takeId);
      })
      .catch(() => {
        this.loadingPromises.delete(takeId);
        this.cleanupTake(takeId);
      });

    return loadPromise;
  }

  /**
   * Preload audio from an streamed blob URL (e.g., from TTS generation stream)
   */
  async loadFromBlobUrl(
    takeId: string,
    blobUrl: string,
    mime?: string | null,
    sectionId?: string | null
  ): Promise<HTMLAudioElement> {
    const existing = this.audioElements.get(takeId);
    if (existing?.audioElement) {
      return existing.audioElement;
    }

    const existingPromise = this.loadingPromises.get(takeId);
    if (existingPromise) {
      return existingPromise;
    }

    const loadPromise = (async () => {
      const audioElement = await this.initializeAudioElement(
        takeId,
        blobUrl,
        mime || null,
        sectionId
      );

      return audioElement;
    })();

    this.loadingPromises.set(takeId, loadPromise);

    loadPromise
      .then(() => {
        this.loadingPromises.delete(takeId);
        store.dispatch(setPreloadedTakeId(takeId));
      })
      .catch(() => {
        this.loadingPromises.delete(takeId);
      });

    return loadPromise;
  }

  /**
   * Load existing audio from the server.
   * Uses MediaSource for supported formats (MP3) to prevent blob exposure in DevTools.
   * Falls back to blob for unsupported formats (WAV, OGG).
   */
  private async loadFromServer(
    takeId: string,
    controller: AbortController,
    headers: StudioAudioHeaders | null | undefined,
    immediatePlayback: boolean,
    sectionId?: string | null,
    silent?: boolean
  ): Promise<HTMLAudioElement> {
    try {
      await this.getAudioAccessToken(takeId);

      const res = await fetch(
        `/api/takes/${encodeURIComponent(takeId)}/audio`,
        {
          headers: headers === null ? undefined : headers,
          signal: controller.signal,
          credentials: 'include',
        }
      );

      this.abortControllers.set(takeId, null);

      if (!res.ok) {
        throw new Error(`Failed to fetch audio (${res.status})`);
      }

      const mime = res.headers.get('Content-Type') || null;

      // Use MediaSource streaming for supported formats to avoid exposing blob in DevTools
      if (this.isMediaSourceSupported(mime)) {
        return this.loadFromServerChunked(
          takeId,
          res,
          mime!,
          sectionId,
          silent
        );
      }

      // Fallback to blob for unsupported formats (WAV, firefox, etc.)
      const blob = await res.blob();
      const url = this.createObjectURL(blob);

      if (!url) {
        throw new Error('createObjectURL not available');
      }
      const audioElement = await this.initializeAudioElement(
        takeId,
        url,
        mime,
        sectionId,
        silent
      );

      return audioElement;
    } catch (error) {
      this.abortControllers.set(takeId, null);

      const err = error instanceof Error ? error : new Error(String(error));

      if (err.name === 'AbortError') {
        throw new Error('Audio load was cancelled');
      }

      // Log the actual error for debugging
      // eslint-disable-next-line no-console
      console.error('[StudioAudioManager] Audio load error:', err.message);

      throw new Error(
        `Take playback failed${
          !immediatePlayback ? ' to load' : ''
        }—check your connection and try again.`
      );
    }
  }

  /**
   * Load audio from server using MediaSource API for obfuscation.
   * Streams chunks directly into MediaSource without creating a downloadable blob.
   */
  private loadFromServerChunked(
    takeId: string,
    response: Response,
    mime: string,
    sectionId?: string | null,
    silent?: boolean
  ): Promise<HTMLAudioElement> {
    return new Promise<HTMLAudioElement>((res, rej) => {
      const mediaSource = new MediaSource();
      const url = URL.createObjectURL(mediaSource);
      const audioElement = new Audio(url);

      const { resolve, reject, isSettled } =
        this.createMediaSourceSettlement<HTMLAudioElement>(
          takeId,
          audioElement,
          url,
          res,
          rej
        );

      let sourceBuffer: SourceBuffer | null = null;
      const chunkQueue: Uint8Array[] = [];
      let isAppending = false;
      let isFinalized = false;
      let hasError = false;
      let streamReader: ReadableStreamDefaultReader<Uint8Array> | null = null;

      const processQueue = () => {
        if (
          hasError ||
          isAppending ||
          !sourceBuffer ||
          sourceBuffer.updating ||
          chunkQueue.length === 0
        ) {
          return;
        }

        isAppending = true;
        const chunk = chunkQueue.shift()!;

        try {
          sourceBuffer.appendBuffer(chunk as BufferSource);
        } catch (error) {
          hasError = true;
          reject(
            new Error(
              `Failed to append buffer: ${
                error instanceof Error ? error.message : String(error)
              }`
            )
          );
        }
      };

      const appendChunk = (chunk: Uint8Array) => {
        if (hasError) return;
        chunkQueue.push(chunk);
        processQueue();
      };

      const finalize = () => {
        if (hasError) return;
        isFinalized = true;

        if (
          chunkQueue.length === 0 &&
          !isAppending &&
          mediaSource.readyState === 'open'
        ) {
          try {
            mediaSource.endOfStream();
          } catch (error) {
            // Ignore errors when ending stream
          }
        }
      };

      const readStream = async () => {
        if (!response.body) {
          reject(new Error('Response body is not readable'));
          return;
        }

        streamReader = response.body.getReader();

        const processStream = async (): Promise<void> => {
          const { done, value } = await streamReader!.read();

          if (done) {
            finalize();
            return;
          }

          if (value) {
            appendChunk(value);
          }

          return processStream();
        };

        try {
          await processStream();
        } catch (error) {
          hasError = true;
          reject(
            new Error(
              `Stream read error: ${
                error instanceof Error ? error.message : String(error)
              }`
            )
          );
        }
      };

      mediaSource.addEventListener('sourceopen', () => {
        // The timeout above may have already rejected this load; a
        // late-firing 'sourceopen' must be a no-op, not resurrect state
        // for a take the caller has already treated as failed.
        if (isSettled()) return;
        try {
          sourceBuffer = mediaSource.addSourceBuffer(mime);

          sourceBuffer.addEventListener('updateend', () => {
            isAppending = false;

            if (
              isFinalized &&
              chunkQueue.length === 0 &&
              mediaSource.readyState === 'open'
            ) {
              try {
                mediaSource.endOfStream();
              } catch (error) {
                // Ignore errors when ending stream
              }
            } else {
              processQueue();
            }
          });

          if (!silent) {
            mediaSource.addEventListener('sourceended', () => {
              const validDuration = this.getValidDuration(
                audioElement.duration
              );
              if (validDuration > 0) {
                store.dispatch(
                  setAudioPlayerMeta({
                    duration: validDuration,
                  })
                );
              }
            });
          }

          sourceBuffer.addEventListener('error', () => {
            hasError = true;
            reject(new Error('SourceBuffer error occurred'));
          });

          const entry: AudioEntry = {
            audioElement,
            url,
            mime,
            takeId,
            sectionId: sectionId ?? null,
          };
          this.audioElements.set(takeId, entry);
          if (!silent) {
            this.setupEventListeners(takeId, audioElement);
          }

          readStream();

          resolve(audioElement);
        } catch (error) {
          hasError = true;
          reject(
            new Error(
              `Failed to setup MediaSource: ${
                error instanceof Error ? error.message : String(error)
              }`
            )
          );
        }
      });

      mediaSource.addEventListener('error', () => {
        hasError = true;
        reject(new Error('MediaSource error occurred'));
      });

      audioElement.addEventListener(
        'error',
        () => {
          hasError = true;
          reject(new Error('Audio element error occurred'));
        },
        { once: true }
      );
    });
  }

  /**
   * Play audio for a take ID
   * @param takeId - The take ID to play
   * @param reset - Whether to reset playback to the beginning
   * @param sectionId - Optional section ID (will be looked up from cache if not provided)
   */
  async play(
    takeId: string,
    reset?: boolean,
    sectionId?: string | null
  ): Promise<void> {
    const shouldReset = reset ?? false;
    store.dispatch(setLoadingTakeId(takeId));

    try {
      await this.attemptPlayback(takeId, shouldReset, sectionId, false);
    } catch (error) {
      if (this.isBenignPlayInterruption(error)) {
        return;
      }

      if (!this.playRetries.has(takeId)) {
        this.playRetries.add(takeId);
        const original =
          error instanceof Error ? error : new Error(String(error));
        // eslint-disable-next-line no-console
        console.error(
          '[StudioAudioManager] Cached take failed to play; evicting and reloading from server',
          { takeId, sectionId: sectionId ?? undefined, error: original }
        );
        captureExceptionWithState(original, {
          type: 'StudioAudioManager',
          value: {
            takeId,
            sectionId: sectionId ?? undefined,
            phase: 'evict-and-reload',
          },
        });
        try {
          this.cleanupTake(takeId);
          await this.attemptPlayback(takeId, shouldReset, sectionId, true);
          return;
        } catch (retryError) {
          if (this.isBenignPlayInterruption(retryError)) {
            this.stopPlayback(takeId);
          } else {
            this.reportPlayFailure(takeId, retryError, sectionId);
          }
          return;
        } finally {
          this.playRetries.delete(takeId);
        }
      }

      this.reportPlayFailure(takeId, error, sectionId);
    } finally {
      store.dispatch(setLoadingTakeId(null));
    }
  }

  private async attemptPlayback(
    takeId: string,
    shouldReset: boolean,
    sectionId: string | null | undefined,
    forceReload: boolean
  ): Promise<void> {
    let audioElement = forceReload
      ? undefined
      : this.audioElements.get(takeId)?.audioElement;

    if (audioElement && audioElement.error) {
      // eslint-disable-next-line no-console
      console.error(
        '[StudioAudioManager] Dropping cached element with media error before playback',
        {
          takeId,
          sectionId: sectionId ?? undefined,
          code: audioElement.error.code,
        }
      );
      this.cleanupTake(takeId);
      audioElement = undefined;
    }

    if (!audioElement) {
      audioElement = await this.loadAudio(takeId, {
        immediatePlayback: true,
        sectionId,
      });
    }

    if (sectionId !== undefined) {
      const entry = this.audioElements.get(takeId);
      if (entry) {
        entry.sectionId = sectionId;
      }
    }

    if (!this.eventListeners.has(takeId)) {
      this.setupEventListeners(takeId, audioElement);
    }

    if (shouldReset) {
      audioElement.currentTime = 0;
      this.pauseTimes.set(takeId, 0);
    }

    store.dispatch(setPreloadedTakeId(takeId));
    store.dispatch(setAudioPlayerMeta({ error: false }));

    await this.startPlayback(takeId, audioElement, sectionId);
  }

  private reportPlayFailure(
    takeId: string,
    error: unknown,
    sectionId?: string | null
  ): void {
    this.stopPlayback(takeId);
    const err = error instanceof Error ? error : new Error(String(error));
    const isCancelled = err.message.includes('cancelled');

    // eslint-disable-next-line no-console
    console.error('[StudioAudioManager] Playback failed', {
      takeId,
      sectionId: sectionId ?? undefined,
      error: err,
    });

    if (!isCancelled) {
      captureExceptionWithState(err, {
        type: 'StudioAudioManager',
        value: {
          takeId,
          sectionId: sectionId ?? undefined,
          phase: 'play-failed',
        },
      });
      toast({
        message: err.message,
        type: 'error',
      });
    }

    store.dispatch(setAudioPlayerMeta({ error: true }));
  }

  /**
   * Pause audio (current global or specific take)
   */
  pause(takeId?: string): void {
    const targetId = takeId || store.getState().scriptStudio.playingTakeId;

    if (!targetId) return;

    const audioElement = this.audioElements.get(targetId)?.audioElement;
    if (audioElement) {
      audioElement.pause();
      this.pauseTimes.set(targetId, audioElement.currentTime || 0);

      this.stopPlayback(targetId);
    }
  }

  unload(): void {
    store.dispatch(setPreloadedTakeId(null));
  }

  /**
   * Dispose of audio element and free memory
   */
  dispose(takeId: string): void {
    this.cleanupTake(takeId);

    if (store.getState().scriptStudio.preloadedTakeId === takeId) {
      store.dispatch(setPreloadedTakeId(null));
      store.dispatch(setPlayingTake({ takeId: null, sectionId: null }));
      store.dispatch(
        setAudioPlayerMeta({ error: false, currentTime: 0, duration: 0 })
      );
    }

    if (store.getState().scriptStudio.loadingTakeId === takeId) {
      store.dispatch(setPreloadedTakeId(null));
      store.dispatch(setLoadingTakeId(null));
    }
  }

  private cleanupTake(takeId: string): void {
    if (this.currentlyPlayingTakeId === takeId) {
      this.currentlyPlayingTakeId = null;
    }

    // Cleanup event listeners first
    this.cleanupEventListeners(takeId);

    // Abort any in-flight request
    this.abortControllers.get(takeId)?.abort();
    this.abortControllers.delete(takeId);
    // Clear loading promise (if any) - this will cause any waiting calls to fail
    this.loadingPromises.delete(takeId);

    const entry = this.audioElements.get(takeId);
    if (entry?.audioElement) {
      const { audioElement } = entry;
      audioElement.pause();
      audioElement.src = '';
      audioElement.srcObject = null;
      audioElement.remove();

      // Revoke object URL to free memory
      if (entry.url) {
        try {
          URL.revokeObjectURL(entry.url);
        } catch (e) {
          // Ignore errors
        }
      }
    }

    this.audioElements.delete(takeId);
    this.pauseTimes.delete(takeId);
  }

  /**
   * Dispose all audio elements and reset state
   */
  disposeAll(): void {
    Array.from(this.audioElements.keys()).forEach(takeId => {
      this.dispose(takeId);
    });

    this.abortControllers.clear();
    this.loadingPromises.clear();
    this.pauseTimes.clear();
    this.eventListeners.clear();

    store.dispatch(setPreloadedTakeId(null));
    store.dispatch(setPlayingTake({ takeId: null, sectionId: null }));
    store.dispatch(
      setAudioPlayerMeta({ error: false, currentTime: 0, duration: 0 })
    );
    store.dispatch(setLoadingTakeId(null));
  }

  /**
   * Get audio element for a take
   */
  getAudioElement(takeId: string): HTMLAudioElement | undefined {
    return this.audioElements.get(takeId)?.audioElement;
  }

  /**
   * Helper to create object URL
   */
  private createObjectURL(blob: Blob): string | undefined {
    if (isBrowser && typeof URL.createObjectURL === 'function') {
      return URL.createObjectURL(blob);
    }
    return undefined;
  }

  /**
   * Check if MediaSource API supports the given MIME type
   */
  private isMediaSourceSupported(mime: string | null): boolean {
    if (!mime || !isBrowser || typeof MediaSource === 'undefined') {
      return false;
    }
    return MediaSource.isTypeSupported(mime);
  }

  /**
   * Request audio access token (sets http-only cookie)
   */
  private async getAudioAccessToken(takeId: string): Promise<void> {
    const client = getApolloClient();
    if (!client) {
      throw new Error('Apollo client not initialized');
    }

    const result = await client.mutate<
      CreateAudioAccessTokenMutation,
      CreateAudioAccessTokenMutationVariables
    >({
      mutation: CreateAudioAccessTokenDocument,
      variables: {
        inputs: { takeId },
      },
    });

    if (result.errors?.length) {
      throw new Error(result.errors[0].message);
    }

    if (!result.data?.createAudioAccessToken.success) {
      throw new Error(
        result.data?.createAudioAccessToken.message ||
          'Failed to get audio access token'
      );
    }
  }
}

// Export singleton instance
export default StudioAudioManager.getInstance();
